import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';
import LockIcon from '@material-ui/icons/Lock';
import Box from '@material-ui/core/Box';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

/**
 * Framework selector for the Day-2 Technology Stack page implementing the
 * three cases:
 *   A — capability existed at Day-1 and is still on  → locked (disabled), kept
 *       at its original framework.
 *   C — capability is newly enabled during this modification → editable.
 * (Case B — capability turned off — is handled by the schema: the framework
 * property is not rendered at all when its enable_* toggle is false.)
 *
 * The owning capability is passed via `ui:options.capability`, e.g.
 * `ui:options: { capability: enable_app_service }`.
 */
export const Day2FrameworkField = (
  props: FieldExtensionComponentProps<string>,
) => {
  const { formData, onChange, schema, uiSchema, rawErrors } = props;
  const fullFormData =
    ((props as any).formContext?.formData as Record<string, any>) ?? {};
  const original = fullFormData._original as Record<string, any> | undefined;

  const capability = (uiSchema?.['ui:options']?.capability as string) ?? '';
  // Locked only on Day-2 (has _original) when the capability already existed.
  const locked = !!original && !!capability && !!original[capability];

  const options: string[] = Array.isArray(schema.enum)
    ? (schema.enum as string[])
    : [];
  const labels: string[] = Array.isArray((schema as any).enumNames)
    ? ((schema as any).enumNames as string[])
    : options;

  const title = schema.title ?? 'Framework';

  return (
    <FormControl fullWidth error={!!rawErrors?.length}>
      <InputLabel shrink>{title}</InputLabel>
      <Select
        value={formData ?? ''}
        disabled={locked}
        onChange={e => onChange(e.target.value as string)}
        displayEmpty
      >
        {options.map((opt, i) => (
          <MenuItem key={opt} value={opt}>
            {labels[i] ?? opt}
          </MenuItem>
        ))}
      </Select>
      {locked ? (
        <Box display="flex" alignItems="center" mt={0.5}>
          <LockIcon fontSize="small" style={{ marginRight: 6, opacity: 0.6 }} />
          <FormHelperText style={{ margin: 0 }}>
            This {title.toLowerCase()} was chosen at creation and cannot be
            changed. Turn the resource off on the previous page to remove it.
          </FormHelperText>
        </Box>
      ) : (
        <FormHelperText>
          {schema.description ?? 'Choose the framework for this newly added resource.'}
        </FormHelperText>
      )}
      {!!rawErrors?.length && (
        <FormHelperText error>{rawErrors[0]}</FormHelperText>
      )}
    </FormControl>
  );
};
